import { useState } from 'react';

import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useWorkflows, useWorkflowExecutionLogs } from '@/hooks/useWorkflows';
import { formatDateTime } from '@/utils/format';

const getTriggerBadge = (triggerType: string) => {
  switch (triggerType) {
    case 'call':
      return <Badge variant="default">{triggerType}</Badge>;
    case 'webhook':
      return <Badge variant="outline">{triggerType}</Badge>;
    case 'scheduled':
      return <Badge variant="secondary">{triggerType}</Badge>;
    case 'dealerkit':
      return <Badge variant="outline">DealerKit</Badge>;
    default:
      return <Badge variant="default">{triggerType}</Badge>;
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'completed':
      return (
        <Badge variant="default" className="bg-green-600 hover:bg-green-600/80">
          {status}
        </Badge>
      );
    case 'failed':
      return <Badge variant="destructive">{status}</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

export const ExecutionHistoryPage = () => {
  usePageTitle('Execution History');

  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [workflowId, setWorkflowId] = useState<string | undefined>(undefined);

  const { data: workflows } = useWorkflows();
  const { data, isLoading } = useWorkflowExecutionLogs({
    page,
    limit,
    workflowId,
  });

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle>Execution History</CardTitle>
          <CardDescription>View workflow execution logs and results</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-center gap-4">
            <Select
              value={workflowId ?? 'all'}
              onValueChange={(val) => {
                setWorkflowId(val === 'all' ? undefined : val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder="Filter by workflow" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Workflows</SelectItem>
                {workflows?.map((wf) => (
                  <SelectItem key={wf.id} value={wf.id}>
                    {wf.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="text-muted-foreground py-8 text-center text-sm">
              Loading execution logs...
            </div>
          ) : !data?.data?.length ? (
            <div className="text-muted-foreground py-8 text-center text-sm">
              No execution logs found.
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow Name</TableHead>
                    <TableHead>Trigger Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Steps</TableHead>
                    <TableHead>Errors</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-8" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((log) => (
                    <TableRow
                      key={log.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/workflows/history/${log.id}`)}
                    >
                      <TableCell className="font-medium">{log.workflow_name}</TableCell>
                      <TableCell>{getTriggerBadge(log.trigger_type)}</TableCell>
                      <TableCell>{getStatusBadge(log.status)}</TableCell>
                      <TableCell>
                        {log.executed_nodes}/{log.total_nodes}
                      </TableCell>
                      <TableCell>
                        {log.errors > 0 ? (
                          <span className="font-medium text-red-600 dark:text-red-400">
                            {log.errors}
                          </span>
                        ) : (
                          log.errors
                        )}
                      </TableCell>
                      <TableCell>{formatDateTime(log.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <ChevronRight className="text-muted-foreground ml-auto h-4 w-4" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="mt-4 flex items-center justify-between">
                <div className="text-muted-foreground text-sm">
                  Page {data.page} of {data.totalPages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= data.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
